import { Component } from 'react';
import api from '../api/client';
import { Sentry } from '../utils/sentryInit';
import { Button } from './ui/button';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

/**
 * Top-level React Error Boundary.
 * Catches render-time crashes anywhere in the tree, ships the stack to
 * /api/v1/activity-logs/errors, and shows a friendly fallback UI instead of
 * the white-screen-of-death.
 *
 * window.onerror (errorReporter.js) does NOT catch React rendering errors —
 * they're swallowed by React's internals — so this is the dedicated path.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: error?.message || 'Something went wrong' };
  }

  componentDidCatch(error, info) {
    // Best-effort report — never let logging itself blow up
    try {
      const route = (window.location?.pathname || '/') + (window.location?.hash || '');
      api.post('/activity-logs/errors', {
        message: String(error?.message || error || 'React render error').slice(0, 2000),
        stack_trace: [error?.stack, info?.componentStack].filter(Boolean).join('\n').slice(0, 8000),
        route,
        severity: 'error',
        user_agent: navigator?.userAgent?.slice(0, 400),
      }).catch(() => {});
    } catch (_e) { /* swallow */ }
    // Sentry — no-op when SENTRY_DSN is unset (most preview/dev envs)
    try {
      Sentry.captureException(error, { contexts: { react: { componentStack: info?.componentStack } } });
    } catch (_e) { /* swallow */ }
    // Console for dev visibility
    if (process.env.NODE_ENV !== 'production') {
      console.error('[ErrorBoundary]', error, info?.componentStack);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, message: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="error-boundary-fallback">
        <div className="max-w-md w-full text-center space-y-4 border rounded-lg p-8 bg-card shadow-sm">
          <div className="mx-auto w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-amber-600" />
          </div>
          <h1 className="font-display text-xl font-semibold tracking-tight">{'Something broke on our side'}</h1>
          <p className="text-sm text-muted-foreground break-words">
            {this.state.message || 'An unexpected error happened while rendering this page.'}
          </p>
          <p className="text-xs text-muted-foreground">
            {'The Lumyn team has been notified. You can try again or head back to the dashboard.'}
          </p>
          <div className="flex gap-2 justify-center pt-2">
            <Button onClick={this.handleReset} variant="outline" size="sm" data-testid="error-boundary-retry">
              <RefreshCw className="w-4 h-4 mr-1" />Retry
            </Button>
            <Button asChild size="sm" data-testid="error-boundary-home">
              <a href="/"><Home className="w-4 h-4 mr-1" />Dashboard</a>
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
